import React, { useState } from 'react';
import { Icon, Button, Panel, SageTokens } from '@kajabi/sage-react';
import AutoHeightTextarea from 'common/components/AutoHeightTextarea';

import { useFormikContext } from 'formik';
import { PromptFormValuesType } from 'common/components/CopyMagic/CopyMagic.types';
import { HIDE_INPUT_FORM_DISCLAIMER_COUNT_THRESHOLD } from 'common/components/CopyMagic/constants';
import { useUiResourceContext } from 'common/components/CopyMagic/contexts';
import useConversationAssistantOpenedCountQuery from 'common/components/CopyMagic/hooks/useConversationAssistantOpenedCountQuery';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import { markAiDisclaimerViewed } from 'common/components/CopyMagic/api';
import CharacterCounter from 'common/components/CharacterCounter';

import { CHAT_MAX_LENGTH } from 'common/components/CopyMagic/shared/validationSchema';
import RestartButton from '../RestartButton/RestartButton';
import useAiConsent from '../../hooks/useAiConsent';

import './style.scss';

const InputForm = () => {
  const { errors, handleBlur, handleChange, values, isValid, submitForm, dirty } =
    useFormikContext<PromptFormValuesType>();
  const [inputClickedEventTracked, setInputClickedEventTracked] = useState(false);
  const { loading } = useUiResourceContext();
  const { aiConsentRequired } = useAiConsent();
  const trackInputFieldClicked = useGeneratorTrackingCallback('useTrackInputFieldClicked');
  // validate but don't show an error if it's just the topic being too short
  const hasError = dirty && !!errors.topic && errors.topic.indexOf('is too short') === -1;

  const handleClick = () => {
    if (inputClickedEventTracked === false) {
      trackInputFieldClicked();
      setInputClickedEventTracked(true);
      if (aiConsentRequired) {
        markAiDisclaimerViewed();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const cmdEnter = e.metaKey && e.key === 'Enter';
    const ctrlEnter = e.ctrlKey && e.key === 'Enter';
    const shiftEnter = e.shiftKey && e.key === 'Enter';
    const altEnter = e.altKey && e.key === 'Enter';

    if (cmdEnter || ctrlEnter || shiftEnter || altEnter) {
      // TODO: Add logic to add a new line for all modifier combinations,
      // for now do nothing here and allow default textarea behavior, which
      // adds a return for shiftEnter and does nothing for other combinations.
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submitForm();
    }
  };

  useConversationAssistantOpenedCountQuery();
  const { conversationAssistantOpenedCount } = useUiResourceContext();
  const shouldShowInputFormDisclaimer =
    conversationAssistantOpenedCount < HIDE_INPUT_FORM_DISCLAIMER_COUNT_THRESHOLD;

  return (
    <div>
      <AutoHeightTextarea
        id="topic"
        placeholder="Ask anything or get started with a suggestion..."
        data-testid="topic"
        disabled={loading}
        defaultFieldValue=""
        value={values.topic}
        onClick={handleClick}
        onChange={handleChange}
        onBlur={handleBlur}
        hasError={hasError}
        onKeyDown={handleKeyDown}
      />

      {shouldShowInputFormDisclaimer && (
        <small data-testid="disclaimer">
          Ama can make mistakes. Consider checking the&nbsp;
          <a href="https://help.kajabi.com/hc/en-us" target="_blank" rel="noopener noreferrer">
            help center
          </a>
          &nbsp;for important info.
        </small>
      )}

      <Panel.Row gridTemplate={SageTokens.GRID_TEMPLATES.TE}>
        <RestartButton />
        <Button.Group gap={Button.Group.GAP_OPTIONS.SM}>
          <CharacterCounter value={values.topic} maxLength={CHAT_MAX_LENGTH} />
          <Button
            disabled={!!loading || !isValid}
            color={Button.COLORS.PRIMARY}
            icon={Icon.ICONS.SEND_MESSAGE}
            iconOnly
            onClick={submitForm}
          >
            Set
          </Button>
        </Button.Group>
      </Panel.Row>
    </div>
  );
};

export default InputForm;
